import { Checkbox, DatePicker, Form, Input, Select, Spin, Table, message } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { serverFetch } from "../../server";
import { useSelector } from "react-redux";
import LogUserItem from "./log-user-item";
import { getLogUserResult } from "../../extensions/helper";
import "./logs.css";
import { tableRenderDateTime } from "../../extensions/table-render";
import { RefreshButton } from "../../extensions/design";
import { BtnSettingsTable, tblGetColumns } from "../../controls/table-settings.js/table-settings";

const tableName = "logs";
/** Размер порции записей, подгружаемой за один запрос */
const PAGE_SIZE = 50;
/** Отступ от низа страницы (px), при достижении которого подгружается следующая порция */
const SCROLL_THRESHOLD = 200;

const Logs = () => {
    const user = useSelector((s) => s.user);
    const [filter,setFilter] = useState({
        begPer: dayjs(),
        endPer: dayjs().add(1, 'day'),
        searchStr: "",
        filter: 0
    })
    const [items, setItems] = useState([]);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [vids, setVids] = useState([]);
    const [item, setItem] = useState();
    const [objectVids, setObjectVids] = useState([]);
    const [allCompanies, setAllCompanies] = useState(false);
    const [tableKey, setTableKey] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    // Актуальное состояние для обработчика скролла (чтобы не зависеть от замыканий)
    const stateRef = useRef({ loading: false, hasMore: true, count: 0 });
    // Номер последнего запроса — ответы устаревших запросов (после смены фильтра) игнорируются
    const requestIdRef = useRef(0);

    useEffect(() => {
        serverFetch("loguser/vids")
            .then(data => setVids(data))
            .catch(e => console.log(e.message));
        serverFetch("loguser/objectvids")
            .then(data => setObjectVids(data))
            .catch(e => console.log(e.message));
    }, [])

    /** Загрузка порции записей. reset=true — с начала (смена фильтра / обновление), иначе — следующая порция */
    const getData = useCallback((reset) => {
        if (stateRef.current.loading) return;
        const offset = reset ? 0 : stateRef.current.count;
        const requestId = ++requestIdRef.current;
        stateRef.current.loading = true;
        setLoading(true);
        const params = new URLSearchParams({
            begPer: filter.begPer.format('YYYY-MM-DD'),
            endPer: filter.endPer.format('YYYY-MM-DD'),
            searchStr: filter.searchStr,
            limit: PAGE_SIZE,
            offset,
        });
        if (filter.vid) params.append('vid', filter.vid);
        if (allCompanies) params.append('allCompanies', 'true');
        serverFetch(`LogUser?${params.toString()}`, { method: 'GET' }, user)
            .then((data) => {
                if (requestId !== requestIdRef.current) return;
                const page = data || [];
                setItems(prev => reset ? page : [...prev, ...page]);
                const more = page.length === PAGE_SIZE;
                stateRef.current = { loading: false, hasMore: more, count: offset + page.length };
                setHasMore(more);
                setLoading(false);
            })
            .catch((e) => {
                if (requestId !== requestIdRef.current) return;
                stateRef.current.loading = false;
                setLoading(false);
                e.UserMessage && message.error(e.UserMessage);
            });
    }, [filter, allCompanies, user]);

    const reload = useCallback(() => {
        form.setFieldsValue(filter);
        stateRef.current = { loading: false, hasMore: true, count: 0 };
        setHasMore(true);
        getData(true);
    }, [getData, form, filter]);

    useEffect(reload, [reload]);

    // Подгрузка следующей порции при прокрутке страницы к низу
    useEffect(() => {
        const onScroll = () => {
            if (stateRef.current.loading || !stateRef.current.hasMore) return;
            const scrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - SCROLL_THRESHOLD;
            if (scrolledToBottom) getData(false);
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [getData]);

    // Если загруженные записи не заполнили экран, скролла нет — догружаем, пока есть что грузить
    useEffect(() => {
        if (loading || !hasMore || items.length === 0) return;
        if (document.documentElement.scrollHeight <= window.innerHeight + SCROLL_THRESHOLD) getData(false);
    }, [items, loading, hasMore, getData]);

    const getLogUserVidName = (value) => {
        const vid = vids && vids.find(i => i.id === value);
        return vid ? vid.name : `[${value}]`
    }

    const getLogUserVidObjectName = (value) => {
        const vid = objectVids && objectVids.find(i => i.id === value);
        return vid ? vid.name : `[${value}]`
    }

    const baseColumns = [
        { title: '#', dataIndex: 'id', width: "1em", align: 'center' },
        { title: 'Дата', dataIndex: 'createdOn', width: "1em", align: 'center', render: tableRenderDateTime },
        { title: 'Вид', dataIndex: 'vid', width: "1em", align: 'left', render: (_,record) => getLogUserVidName(record.vid) },
        { title: 'Клиент', dataIndex: 'clientName', width: "1em", align: 'left' },
        { title: 'Телефон', dataIndex: 'clientPhone', width: "1em", align: 'left' },
        { title: 'Компания', dataIndex: 'companyName', width: "1em", align: 'left' },
        { title: 'Пользователь', dataIndex: 'userName', width: "1em", align: 'left' },
        { title: 'Комментарий', dataIndex: 'comment', width: "1em", align: 'left' },
        { title: 'Результат', dataIndex: 'result', width: "1em", align: 'center', render: (value) => getLogUserResult(value) },
        { title: 'Объект', dataIndex: 'objectVid', width: "1em", align: 'left', render: (_,record) => getLogUserVidObjectName(record.objectVid)  },
        { title: 'Объект #', dataIndex: 'objectId', width: "1em", align: 'left' },
    ];

    const columns = tblGetColumns(baseColumns, tableName);

    const onCloseItem = () => setItem();
    // Поиск по SearchStr и фильтр по виду делает API; записи подгружаются порциями при скролле.
    const vidOptions = vids.map(i => ({label: `[${i.id}] ${i.name}`, value: i.id}))

    const onValuesChange = value =>  setFilter({ ...filter, ...value }); 
    const onTableSettingsChange = () => setTableKey(prev => prev + 1);
    
    return<div className="sport-form-content logs-container">
        <div className="sport-form-title">
            <div>Период с</div>
            <DatePicker format="DD.MM.YYYY" allowClear={false} value={filter.begPer} onChange={e => onValuesChange({begPer: e})}/>
            <div>по</div>
            <DatePicker format="DD.MM.YYYY" allowClear={false} value={filter.endPer} onChange={e => onValuesChange({endPer: e})}/>
            <Select options={vidOptions} style={{width: 400}} allowClear value={filter.vid} onChange={e => onValuesChange({vid: e})}/>
            <Input
                className="logs-search"
                placeholder="Поиск: #, объект #, клиент, телефон, комментарий"
                value={filter.searchStr}
                onChange={e => onValuesChange({searchStr: e.target.value})}
            />
            {user.isSuperadmin && (
                <Checkbox checked={allCompanies} onChange={e => setAllCompanies(e.target.checked)}>
                    По всем компаниям
                </Checkbox>
            )}
            <RefreshButton onClick={reload} />
            <BtnSettingsTable name={tableName} columns={baseColumns} onSettingsChange={onTableSettingsChange}/>
        </div>
        <Table 
            key={tableKey}
            dataSource={items} 
            columns={columns} 
            pagination={false}
            rowKey="id"
            loading={loading && items.length === 0}
            onRow={(record) => {return {onClick: () => {setItem(record)}}} }
            size="small"
        />
        {loading && items.length > 0 && <div className="logs-loading-more"><Spin size="small" /> Загрузка...</div>}
        {item && <LogUserItem id={item.id} onClose={onCloseItem} />}
    </div>
}

export default Logs;